/**
 * WorldManager - Manages multiple worlds (overworld and hell)
 */

import { Vector3 } from '../math/Vector3';
import { World, WorldType } from './World';
import { BuildingSystem } from '../building/BuildingSystem';
import { EnemySpawner } from '../combat/EnemySpawner';
import { HellEnemySpawner } from '../combat/HellEnemySpawner';
import { Player } from '../entities/Player';

export interface WorldData {
  world: World;
  buildingSystem: BuildingSystem;
  enemySpawner: EnemySpawner | HellEnemySpawner;
  playerLastPosition: Vector3;
}

export class WorldManager {
  private worlds: Map<string, WorldData> = new Map();
  private currentWorldId: string = 'overworld';
  private baseSeed: number;

  constructor(seed: number = Date.now()) {
    this.baseSeed = seed;
  }

  /** Initialize the overworld */
  initializeOverworld(): WorldData {
    const world = new World(this.baseSeed, 100, 'overworld');
    const buildingSystem = new BuildingSystem();
    const enemySpawner = new EnemySpawner(world);

    const worldData: WorldData = {
      world,
      buildingSystem,
      enemySpawner,
      playerLastPosition: new Vector3(0, 0, 0)
    };

    this.worlds.set('overworld', worldData);
    this.currentWorldId = 'overworld';
    return worldData;
  }

  /** Initialize the hell world (lazy - only when first accessed) */
  initializeHellWorld(): WorldData {
    // Use a different seed for hell based on overworld seed
    const hellSeed = this.baseSeed + 666;
    const world = new World(hellSeed, 100, 'hell');
    const buildingSystem = new BuildingSystem();

    // Hell world uses HellEnemySpawner (only demons and ghosts)
    const enemySpawner = new HellEnemySpawner(world);

    const worldData: WorldData = {
      world,
      buildingSystem,
      enemySpawner,
      playerLastPosition: new Vector3(0, 0, 0)
    };

    this.worlds.set('hell', worldData);
    return worldData;
  }

  /** Get or create a world by ID */
  getOrCreateWorld(worldId: string): WorldData {
    let worldData = this.worlds.get(worldId);
    if (!worldData) {
      if (worldId === 'hell') {
        worldData = this.initializeHellWorld();
      } else {
        // Default to overworld
        worldData = this.initializeOverworld();
      }
    }
    return worldData;
  }

  /** Get current world data */
  getCurrentWorldData(): WorldData {
    return this.getOrCreateWorld(this.currentWorldId);
  }

  /** Get current world */
  getCurrentWorld(): World {
    return this.getCurrentWorldData().world;
  }

  /** Get current building system */
  getCurrentBuildingSystem(): BuildingSystem {
    return this.getCurrentWorldData().buildingSystem;
  }

  /** Get current enemy spawner */
  getCurrentEnemySpawner(): EnemySpawner | HellEnemySpawner {
    return this.getCurrentWorldData().enemySpawner;
  }

  /** Get current world ID */
  getCurrentWorldId(): string {
    return this.currentWorldId;
  }

  /** Get world type */
  getCurrentWorldType(): WorldType {
    return this.getCurrentWorld().worldType;
  }

  /** Save player position in current world */
  savePlayerPosition(position: Vector3): void {
    const worldData = this.worlds.get(this.currentWorldId);
    if (worldData) {
      worldData.playerLastPosition = position.clone();
    }
  }

  /** Get spawn position for a world */
  getSpawnPosition(worldId: string): Vector3 {
    const worldData = this.worlds.get(worldId);
    if (worldData && (worldData.playerLastPosition.x !== 0 || worldData.playerLastPosition.y !== 0)) {
      return worldData.playerLastPosition.clone();
    }
    // Default spawn position
    return new Vector3(0, 0, 0);
  }

  /** Switch to a different world */
  switchWorld(targetWorldId: string, player: Player): Vector3 {
    // Save current position
    this.savePlayerPosition(player.position);

    // Get or create target world
    const targetWorldData = this.getOrCreateWorld(targetWorldId);

    // Update current world ID
    this.currentWorldId = targetWorldId;

    // Set up enemy spawner with player
    targetWorldData.enemySpawner.setPlayer(player);

    // Return spawn position in new world
    return this.getSpawnPosition(targetWorldId);
  }

  /** Check if hell world exists */
  hasHellWorld(): boolean {
    return this.worlds.has('hell');
  }

  /** Get all world IDs */
  getWorldIds(): string[] {
    return Array.from(this.worlds.keys());
  }

  /** Get world data by ID */
  getWorldData(worldId: string): WorldData | undefined {
    return this.worlds.get(worldId);
  }

  /** Update current world */
  update(deltaTime: number, dayLength: number = 300): void {
    const worldData = this.getCurrentWorldData();
    worldData.world.update(deltaTime, dayLength);
    worldData.buildingSystem.update(deltaTime);
    worldData.enemySpawner.update(deltaTime);
  }

  /** Set night mode for current world's enemy spawner */
  setNight(isNight: boolean): void {
    this.getCurrentEnemySpawner().setNight(isNight);
  }

  /** Get the base seed used for world generation */
  getBaseSeed(): number {
    return this.baseSeed;
  }

  /** Set base seed (for loading saves) */
  setBaseSeed(seed: number): void {
    this.baseSeed = seed;
  }

  /** Clear all worlds (for new game) */
  clearAllWorlds(): void {
    // Clear enemies in all worlds
    for (const worldData of this.worlds.values()) {
      worldData.enemySpawner.clearAllEnemies();
    }
    this.worlds.clear();
    this.currentWorldId = 'overworld';
  }

  /** Restore a world from save data */
  restoreWorld(
    worldId: string,
    seed: number,
    worldType: WorldType,
    playerLastPosition: { x: number; y: number; z: number }
  ): WorldData {
    const world = new World(seed, 100, worldType);
    const buildingSystem = new BuildingSystem();

    // Use appropriate enemy spawner based on world type
    const enemySpawner = worldType === 'hell'
      ? new HellEnemySpawner(world)
      : new EnemySpawner(world);

    const worldData: WorldData = {
      world,
      buildingSystem,
      enemySpawner,
      playerLastPosition: new Vector3(
        playerLastPosition.x,
        playerLastPosition.y,
        playerLastPosition.z
      )
    };

    this.worlds.set(worldId, worldData);
    return worldData;
  }
}
